import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { Badge, EmptyState } from '@enamelai/design-system'
import { Provider } from '@dataTypes/provider.types'
import AddProviderModal from '@components/layout/Modals/AddProviderModal/AddProviderModal.component'
import { mapDatabaseToProviderData } from '@utils/provider.mapper'
import { ReactComponent as FamilyEditIcon } from '@assets/images/Sidepanel/family-edit.svg'
import { useAppSelector } from '@store/hooks'
import { useClinicClosedWeekdays } from '@components/pages/Settings/Staff/WorkSchedule/hooks/useClinicClosedWeekdays'
import {
  buildWeek,
  weekdayHours,
  type DayShift,
} from '@components/pages/Settings/Staff/WorkSchedule/staffShifts.util'

type WorkShiftProps = {
  provider: Provider | null
}

const getShiftMinutes = (start?: string, end?: string): number => {
  if (!start || !end) return 0
  const toMinutes = (value: string): number => {
    const [h, m] = value.split(':')
    return parseInt(h, 10) * 60 + (parseInt(m, 10) || 0)
  }
  const diff = toMinutes(end) - toMinutes(start)
  return Number.isNaN(diff) || diff < 0 ? 0 : diff
}

const isWorkingShift = (shift: DayShift): boolean =>
  shift.kind === 'shift' || shift.kind === 'half-day'

const WorkShift: React.FC<WorkShiftProps> = ({ provider }) => {
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''
  const closedWeekdays = useClinicClosedWeekdays(clinicId)

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        moment().isoWeekday(1).startOf('day').add(i, 'day'),
      ),
    [],
  )

  const weekdayRows = weekdayHours(provider?.workingHours)
  const shifts = useMemo(
    () => (provider ? buildWeek(provider, days, closedWeekdays) : []),
    [closedWeekdays, days, provider],
  )

  const hasSchedule = weekdayRows.length > 0 || shifts.some(isWorkingShift)

  const totalMinutes = weekdayRows.reduce((sum, row) => {
    const start =
      provider?.same_working_hours === true
        ? provider.shift_start_time
        : row.start_time
    const end =
      provider?.same_working_hours === true
        ? provider.shift_end_time
        : row.end_time
    return sum + getShiftMinutes(start, end)
  }, 0)
  const totalHours = Math.round((totalMinutes / 60) * 10) / 10
  const workingDays = shifts.filter(isWorkingShift).length

  const renderScheduleCard = (
    day: moment.Moment,
    shift: DayShift,
  ): React.ReactElement => {
    const isToday = day.isSame(moment(), 'day')
    const isOff = !isWorkingShift(shift)
    const classNames = [
      'sp-schedule-card',
      isToday ? 'sp-schedule-card--today' : '',
      isOff ? 'sp-schedule-card--off' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div key={day.format('YYYY-MM-DD')} className={classNames}>
        <div className="sp-schedule-card-day">
          <span className="sp-schedule-card-day-name">
            {day.format('dddd')}
          </span>
          {isToday && <Badge label="Today" color="blue" mode="solid" />}
        </div>
        <span className="sp-schedule-card-time">
          {shift.label}
          {shift.overrideLabel ? ` (${shift.overrideLabel})` : ''}
        </span>
      </div>
    )
  }
  const handleEditClick = (): void => {
    setShowEditModal(true)
  }

  const handleCloseModal = (): void => {
    setShowEditModal(false)
  }

  return (
    <>
      {hasSchedule ? (
        <div className="work-schedule">
          <section className="sp-details-section sp-details-section--last">
            <div className="sp-details-section-header">
              <span className="sp-details-section-title">Work schedule</span>
              <button
                type="button"
                className="sp-details-section-edit"
                aria-label="Edit work schedule"
                onClick={handleEditClick}
              >
                <FamilyEditIcon
                  className="sp-details-section-edit-svg"
                  aria-hidden
                />
              </button>
            </div>
            <div className="sp-schedule-summary">
              <span>
                {workingDays} working {workingDays === 1 ? 'day' : 'days'}
              </span>
              <span className="sp-schedule-summary-dot" aria-hidden />
              <span>{totalHours} hrs / week</span>
            </div>
            <div className="sp-schedule-list">
              {days.map((day, index) =>
                renderScheduleCard(
                  day,
                  shifts[index] ?? { kind: 'day-off', label: 'Day-off' },
                ),
              )}
            </div>{' '}
          </section>
        </div>
      ) : (
        <EmptyState
          className="sp-history-empty-state"
          iconClass="mgc mgc_calendar_time_add_fill"
          title="No work schedule"
          description="This staff member doesn't have working hours set yet."
          actionLabel={provider ? 'Set work schedule' : undefined}
          onAction={provider ? handleEditClick : undefined}
        />
      )}

      {showEditModal && provider && (
        <AddProviderModal
          isOpen={showEditModal}
          onRequestClose={handleCloseModal}
          providerData={mapDatabaseToProviderData(provider)}
          initialStep={2}
        />
      )}
    </>
  )
}

export default WorkShift
